import { IProcedure } from '../Procedure';
import { yup } from 'common/validation/initYup';
import { WidgetType, IWidget } from '../widget/Widget';
import { reduce, fromPairs, merge, uniq } from 'lodash';
import { WidgetInputType } from '../widget/input/WidgetInputType';
import { MetadataLogic } from '../metadata/MetadataLogic';
import { WidgetInputTextFormatType } from 'common/validation/type/WidgetInputTextFormatType';
import { IRequestFileItem, IRequestValues } from './Request';
import { WidgetInputViewType } from '../widget/input/WidgetInputViewType';
import { assertNever } from 'server/utils/typings/assertNever';
import {
  metadataSuffix,
  SuffixType
} from 'client/components/schema/widget/helper/metadataSuffix';
import { ICaptchaDecryptedTokens } from 'server/captcha/CaptchaService';
import moment from 'moment';
import { WidgetInputFileTypes } from '../widget/input/WidgetUploadFileType';
import { createWidgetInputCascadeSelectValidator } from 'client/components/schema/widget/repository/input-cascade-select/createWidgetInputCascadeSelectValidator';

const B_TO_MB = 1024 * 1024;
const FISCAL_CODE_REGEX = /^(TINIT-)?[A-Za-z]{6}[0-9lmnpqrstuvLMNPQRSTUV]{2}[abcdehlmprstABCDEHLMPRST]{1}[0-9lmnpqrstuvLMNPQRSTUV]{2}[A-Za-z]{1}[0-9lmnpqrstuvLMNPQRSTUV]{3}[A-Za-z]{1}$/;
const VAT_NUMBER_REGEX = /^[0-9]{11}$/;
const PHONE_NUMBER_REGEX = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;

/**
 * Crea dinamicamente il validatore per tutti i metadati (di input) contenuti
 * nella richiesta, a partire dallo schema definito nella Domanda (`Procedure`)
 */
export function createProcedureValuesValidator(procedure: IProcedure) {
  return createWidgetsValuesValidator(procedure.widgets);
}

/**
 * Crea dinamicamente il validatore per tutti i metadati (di input) contenuti
 * nell'elenco dei Widget, sia che siano di primo livello (`IProcedure`) sia
 * che siano annidati (`subwidgets`)
 */
export function createWidgetsValuesValidator(widgets: IWidget[]) {
  const inputs = widgets.filter(w => w.type === WidgetType.Input);
  const validators = merge({}, ...inputs.map(createWidgetInputValidator));

  return yup.object<IRequestValues>(validators).noUnknown().required();
}

/**
 * Crea la validazione di un singolo Widget di Input. Questo potrebbe
 * corrispondere a più metadati, quindi più validatori, per questo ritorniamo
 * un oggetto di tipo:
 *
 *    { [metadataId]: ValidatorSchema }
 */
export function createWidgetInputValidator(widget: IWidget) {
  const input = widget.input!;

  // TODO/HI Completare con la gestione di tutti gli schema disponibili
  switch (input.type) {
    /**
     * Provincia e Comune vengono gestiti nel dettaglio perché hanno più
     * metadati collegati allo stesso Widget.
     */
    case WidgetInputType.Region: {
      let provinciaSchema = yup.string().label('Provincia');
      let comuniSchema = yup.string().label('Comune');

      const [provincia, comune] = MetadataLogic.multipleFromWidget(widget, [
        'provincia',
        'comune'
      ]);

      if (input.required) {
        provinciaSchema = provinciaSchema.requiredMetadata(provincia, input);
        comuniSchema = comuniSchema.requiredMetadata(comune, input);
      }

      return {
        [provincia.id]: provinciaSchema,
        [comune.id]: comuniSchema
      };
    }

    case WidgetInputType.File:
    case WidgetInputType.UploadSigned:
      const metadata = MetadataLogic.singleFromWidget(widget);
      return { [metadata.id]: createFileUploadValidator(widget) };

    case WidgetInputType.Date: {
      let schema = yup.date().label(input.label);
      const metadata = MetadataLogic.singleFromWidget(widget);

      // La validazione della data va definita prima della requiredMetadata cosi da portare uno schema totalmente definito
      // altrimenti viene richiesta l'esecuzione della .when ma con una request ancora non valorizzata
      if (input.validation.datemin)
        schema = schema.min(input.validation.datemin);
      if (input.validation.datemax)
        schema = schema.max(input.validation.datemax);

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      return { [metadata.id]: schema };
    }

    /**
     * Intervallo di date, gestito con doppio validatore su `inizio` e `fine`.
     */
    case WidgetInputType.DateInterval: {
      let inizioSchema = yup.date().label('Data Inizio');
      let fineSchema = yup.date().label('Data Fine');

      const [inizio, fine] = MetadataLogic.multipleFromWidget(widget, [
        'inizio',
        'fine'
      ]);

      if (input.validation.datemax) {
        inizioSchema = inizioSchema.max(input.validation.datemax);
        fineSchema = fineSchema.max(input.validation.datemax);
      }

      if (input.validation.datemin) {
        inizioSchema = inizioSchema.min(input.validation.datemin);
        fineSchema = fineSchema.min(input.validation.datemin);
      }

      // La data deve essere successiva all'inizio
      fineSchema = fineSchema.min(yup.ref(inizio.id));

      if (input.required) {
        inizioSchema.requiredMetadata(inizio, input);
        fineSchema.requiredMetadata(fine, input);
      }

      return {
        [inizio.id]: inizioSchema,
        [fine.id]: fineSchema
      };
    }

    /**
     * Per le scelte singole, validiamo con `oneOf`
     */
    case WidgetInputType.SingleChoice: {
      const metadata = MetadataLogic.singleFromWidget(widget);
      let schema = yup
        .string()
        .label(input.label)
        .oneOf(input.options.map(o => o.value));

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      return { [metadata.id]: schema };
    }

    /**
     * Per le scelte multiple, validiamo con array. Le singole opzioni sono
     * gestite sempre con `oneOf`
     */
    case WidgetInputType.MultipleChoice: {
      let schema = yup
        .array(yup.string().oneOf(input.options.map(o => o.value)))
        .label(input.label);

      if (input.validation.atleast)
        schema = schema.min(input.validation.atleast);

      const metadata = MetadataLogic.singleFromWidget(widget);

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      return { [metadata.id]: schema };
    }

    /**
     * Booleani
     */
    case WidgetInputType.Boolean: {
      let schema = yup.boolean().label(input.label);

      const metadata = MetadataLogic.singleFromWidget(widget);

      if (input.required) {
        schema = schema.requiredMetadata(metadata, input);
      }

      return { [metadata.id]: schema };
    }

    /**
     * Email e Testo si comportano similarmente, ad esclusione del formato
     * (fisso per le email) e per la mail di conferma (che è gestita come
     * fosse un altro metadato)
     */
    case WidgetInputType.Email:
    case WidgetInputType.Text: {
      // Nel caso standard, abbiamo solo un metadato.
      const metadata = MetadataLogic.singleFromWidget(widget);

      let schema = yup.string().trim().label(input.label);

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      if (input.type === WidgetInputType.Email) {
        schema = schema.email();
      }

      if (input.validation.maxlength != null)
        schema = schema.max(input.validation.maxlength);

      if (input.validation.minlength != null)
        schema = schema.min(input.validation.minlength);

      if (input.validation.format === WidgetInputTextFormatType.FiscalCode)
        schema = schema.matches(FISCAL_CODE_REGEX, {
          message: 'Il codice fiscale inserito non è valido',
          excludeEmptyString: true
        });

      if (input.validation.format === WidgetInputTextFormatType.VatNumber)
        schema = schema.matches(VAT_NUMBER_REGEX, {
          message: 'La partita IVA inserita non è valida',
          excludeEmptyString: true
        });

      if (input.validation.format === WidgetInputTextFormatType.PhoneNumber)
        schema = schema.matches(PHONE_NUMBER_REGEX, {
          message: 'Il numero di telefono inserito non è valido',
          excludeEmptyString: true
        });

      const objectSchema = { [metadata.id]: schema };

      // Per l'email metadato di conferma
      if (input.view === WidgetInputViewType.EmailCheck) {
        let confirmSchema = yup
          .string()
          .label(`Conferma ${input.label}`)
          .oneOf(
            [yup.ref(metadata.id)],
            'Le eMail inserite devono essere uguali'
          );

        if (input.required)
          confirmSchema = confirmSchema.requiredMetadata(metadata, input);
        objectSchema[
          `${metadataSuffix(metadata.id, SuffixType.Confirm)}`
        ] = confirmSchema;
      }

      return objectSchema;
    }

    /**
     * Gestione Numerica
     */
    case WidgetInputType.Numeric:
    case WidgetInputType.Currency: {
      let schema = yup.number().label(input.label);
      const metadata = MetadataLogic.singleFromWidget(widget);

      if (input.required) schema = schema.requiredMetadata(metadata, input);
      if (input.validation.min) schema = schema.min(input.validation.min);
      if (input.validation.max) schema = schema.max(input.validation.max);
      if (input.validation.integer) schema = schema.integer();
      if (input.validation.positive) schema = schema.positive();

      return { [metadata.id]: schema };
    }

    /**
     * Gestione delle liste ricorsiva
     */
    case WidgetInputType.List: {
      let schema: yup.NotRequiredArraySchema<any> = yup
        .array(
          yup
            .object({
              values: createWidgetsValuesValidator(input.subwidgets ?? [])
            })
            .required()
        )
        .label(input.label);

      // Per le liste, abbiamo solo un metadato.
      const metadata = MetadataLogic.singleFromWidget(widget);

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      if (input.validation.minrows)
        schema = schema.min(input.validation.minrows);
      if (input.validation.maxrows)
        schema = schema.max(input.validation.maxrows);

      return { [metadata.id]: schema };
    }

    /**
     * Gestione dei Captcha
     */
    case WidgetInputType.Captcha: {
      const metadata = MetadataLogic.singleFromWidget(widget);
      let schema = yup
        .string()
        .required()
        .label(input.label)
        .when(
          '$captcha',
          (captcha: ICaptchaDecryptedTokens, schema: yup.StringSchema) => {
            return captcha
              ? schema
                  .test(
                    'testCaptcha',
                    'Il codice CAPTCHA inserito non è corretto.',
                    (value: string) => {
                      if (!value || typeof value !== 'string') return false;
                      return value === captcha[metadata.id].text;
                    }
                  )
                  .test(
                    'expireDate',
                    'Il CAPTCHA è scaduto, generane uno nuovo e inserisci nuovamente il codice',
                    () =>
                      moment().isBefore(
                        moment(captcha[metadata.id].expirationDate)
                      )
                  )
              : schema;
          }
        );
      return { [metadata.id]: schema };
    }

    case WidgetInputType.Preview:
      return {};

    case WidgetInputType.CascadeSelect:
      return createWidgetInputCascadeSelectValidator(widget);

    case WidgetInputType.MultiPreference: {
      const options = input.extra?.cascade;
      if (!options) return {};
      // Per ogni colonna di preferenze (es. 1, 2, 3, 4, 5) creo un array di valori accettati (es. [1, 2, 3, 4, 5])
      // e un validator per la colonna (es. yup.number().oneOf([1, 2, 3, 4, 5]))
      const arrayFields = options.fields.map((field, i) => {
        const columnAcceptedValues = options.combinations.map(c => c.values[i]);
        const columnValidator = yup
          .string()
          .label(field.label)
          .oneOf(uniq(columnAcceptedValues))
          .required();
        return columnValidator;
      });
      // Creo un oggetto con i validatori per ogni colonna
      // (es. {1: yup.number().oneOf([1, 2, 3, 4, 5]), 2: yup.number().oneOf([1, 2, 3, 4, 5]), ...})
      let schema: yup.NotRequiredArraySchema<any> = yup
        .array(
          yup
            .object({
              values: yup.object(merge({}, arrayFields)).label(input.label)
            })
            .required()
        )
        .label(input.label);

      // Per le liste/preferenze multiple, abbiamo solo un metadato.
      const metadata = MetadataLogic.singleFromWidget(widget);

      schema = input.required
        ? schema.requiredMetadata(metadata, input)
        : schema.notRequired();

      // Se ci sono dei vincoli sul numero di righe, li aggiungo
      if (input.validation?.minrows)
        schema = schema.min(input.validation.minrows);
      if (input.validation?.maxrows)
        schema = schema.max(input.validation.maxrows);

      return { [metadata.id]: schema };
    }
    default:
      assertNever(input.type);
  }
}

export function createWidgetListRowValidator(widgets: IWidget[]) {
  return yup
    .object({
      values: createWidgetsValuesValidator(widgets)
    })
    .required();
}

interface IValidatedFile {
  attachmentId: string | undefined;
  title: string | undefined;
  path: string | undefined;
  fileName: string;
  size: number;
  type: string;
}

export type AttachmentValidationResult = (IValidatedFile | undefined)[];

export function createFileUploadValidator(widget: IWidget) {
  const input = widget.input!;
  const maxsize = input.validation.maxsize ?? 0;
  const defaultFiletypes = Object.values<string[]>(WidgetInputFileTypes)
    .map(t => t.join(','))
    .join(',');
  const validatorFileTypes = input.validation?.filetype?.join(',').split(',');
  const fileTypes =
    validatorFileTypes && validatorFileTypes.length > 0
      ? validatorFileTypes
      : defaultFiletypes.split(',');
  let schema = yup
    .array(
      yup
        .object({
          attachmentId: yup
            .string()
            .notRequired()
            .when('$upload', (upload: any, schema: any) => {
              return upload
                ? schema.strip()
                : schema.required().label('ID Allegato');
            }),
          title: yup
            .string()
            .notRequired()
            .when('$upload', (upload: any, schema: any) => {
              return upload
                ? schema.required().label('Nome file sul server')
                : schema.strip();
            }),
          path: yup.string().when('$upload', (upload: any, schema: any) => {
            return upload
              ? schema.required().label('Path del file sul server')
              : schema.notRequired().strip();
          }),
          fileName: yup.string().required().label('Nome del file allegato'),
          size: yup
            .number()
            .required()
            .label('Dimensione file')
            .test({
              name: 'maxsize',
              exclusive: true,
              params: { maxsize },
              message:
                'Errore: un allegato ha dimensione superiore a ${maxsize} MB.',
              test: value => {
                return maxsize > 0 ? value / B_TO_MB <= maxsize : true;
              }
            }),
          type: yup
            .string()
            .required()
            .test({
              name: 'fileType',
              exclusive: true,
              params: { fileTypes },
              message: "Errore: un allegato ha un'estensione non ammessa.",
              test: (value: string) =>
                fileTypes ? fileTypes.includes(value) : true
            })
            .label('Tipo File')
        })
        .noUnknown()
    )
    .ensure()
    .label(input.label);

  if (input.validation.multiple == false) {
    schema = schema.test({
      name: 'single',
      exclusive: true,
      message: 'Errore: non è possibile caricare allegati multipli.',
      test: value => value.length < 2
    });
  }

  const metadata = MetadataLogic.singleFromWidget(widget);

  // A differenza del File normale, l'`UploadSigned` è sempre richiesto.
  // Tuttavia durante la generazione del documento di anteprima non possiamo
  // richiederlo.
  if (input.type === WidgetInputType.UploadSigned) {
    return schema.when(
      ['$isPreview'],
      (isPreview: boolean, schema: yup.MixedSchema<any>) => {
        return isPreview ? schema.notRequired() : schema.required();
      }
    );
  }

  schema = input.required
    ? schema.requiredMetadata(metadata, input)
    : schema.notRequired();

  return schema;
}
